import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { dsoApi } from '../../services/api';
import { FaSpinner, FaExclamationTriangle, FaSearch, FaFilter, FaTimes, FaEye } from 'react-icons/fa';
import Pagination from '../../components/Pagination';
import Select from 'react-select';
import { Container, Card, Row, Col, Table, Button, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

// Helper function to format dates
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  } catch (e) {
    return dateString; // Fallback
  }
};

// Helper function to format currency
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return 'N/A';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

function DSOInvoices() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [entriesPerPage] = useState(10);
  const [actionLoading, setActionLoading] = useState(null);

  // Filter state
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [filterApplied, setFilterApplied] = useState(false);

  // Status filter options
  const approvalStatusOptions = [
    { value: 'Pending', label: 'Pending' },
    { value: 'Approved', label: 'Approved' },
    { value: 'Rejected', label: 'Rejected' }
  ];

  const paymentStatusOptions = [
    { value: 'Generated', label: 'Generated' },
    { value: 'Completed', label: 'Completed' },
    { value: 'Failed', label: 'Failed' }
  ];

  // Fetch invoices function with filters
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const skip = (currentPage - 1) * entriesPerPage;
      
      // Build filter params
      const params = {
        skip,
        limit: entriesPerPage
      };
      
      // Add filters if they're selected
      if (invoiceNumber.trim()) {
        params.invoice_number = invoiceNumber.trim();
      }
      
      if (approvalStatus) {
        params.approval_status = approvalStatus.value;
      }
      
      if (paymentStatus) {
        params.payment_status = paymentStatus.value;
      }
      
      // Set flag if any filter is applied
      setFilterApplied(
        !!invoiceNumber.trim() || 
        !!approvalStatus || 
        !!paymentStatus
      );
      
      const response = await dsoApi.getInvoices(params);
      setInvoices(response.data.items || []);
      setTotalInvoices(response.data.total || 0);
      setTotalPages(Math.ceil((response.data.total || 0) / entriesPerPage));
    } catch (err) {
      console.error("Error fetching invoices:", err);
      setError('Failed to load invoices. Please try again.');
      // Reset state on error
      setInvoices([]);
      setTotalInvoices(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, entriesPerPage, invoiceNumber, approvalStatus, paymentStatus]);

  // Initial fetch and fetch on page change or filters change
  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Handle page change from Pagination component
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Handle filter form submission
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when applying new filters
    fetchInvoices();
  };

  // Clear all filters
  const clearFilters = () => {
    setInvoiceNumber('');
    setApprovalStatus(null);
    setPaymentStatus(null);
    setCurrentPage(1);
  };

  // Function to render approval status badge
  const renderApprovalStatusBadge = (status) => {
    let badgeClass = 'dso-status-badge ';
    switch (status?.toLowerCase()) {
      case 'approved':
        badgeClass += 'dso-status-approved'; // Green
        break;
      case 'rejected':
        badgeClass += 'dso-status-rejected'; // Red
        break;
      case 'pending': // Assuming 'Pending' is the initial state
      default:
        badgeClass += 'dso-status-pending'; // Yellow/Orange
    }
    return <span className={badgeClass}>{status || 'Pending'}</span>;
  };

  // Function to render payment status badge
  const renderPaymentStatusBadge = (status) => {
    let badgeClass = 'dso-status-badge ';
    switch (status?.toLowerCase()) {
      case 'completed':
        badgeClass += 'dso-payment-status-completed'; // Green
        break;
      case 'failed':
        badgeClass += 'dso-payment-status-failed'; // Red
        break;
      case 'pending': // Initial state
      case 'generated':
      default:
        badgeClass += 'dso-payment-status-generated'; // Blue
    }
    return <span className={badgeClass}>{status || 'Pending'}</span>;
  };

  // Custom styles for react-select
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '36px',
      height: '36px',
      boxShadow: 'none',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '0.85rem',
      '&:hover': {
        border: '1px solid #cbd5e1',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: '36px',
      padding: '0 8px',
      display: 'flex',
      alignItems: 'center',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '36px',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '4px',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: '4px',
    }),
    option: (provided) => ({
      ...provided,
      fontSize: '0.85rem',
      padding: '6px 12px',
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '0.85rem',
      color: '#94a3b8',
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: '0.85rem',
    }),
  };

  return (
    <div className="dso-invoices-page dso-list-page">
      <div className="dso-list-header">
        <h2>My Invoices</h2>
        {filterApplied && (
          <Button 
            variant="outline-secondary" 
            size="sm" 
            className="clear-filters-btn"
            onClick={clearFilters}
          >
            <FaTimes /> Clear Filters
          </Button>
        )}
      </div>

      {/* Filter Section */}
      <div className="filter-section">
        <Form onSubmit={handleFilterSubmit}>
          <Row className="mb-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Invoice Number</Form.Label>
                <Form.Control
                  type="text"
                  value={invoiceNumber}
                  onChange={(e) => setInvoiceNumber(e.target.value)}
                  placeholder="Search invoice #"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Approval Status</Form.Label>
                <Select
                  value={approvalStatus}
                  onChange={setApprovalStatus}
                  options={approvalStatusOptions}
                  isClearable
                  placeholder="Select status"
                  styles={customSelectStyles}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>Payment Status</Form.Label>
                <Select
                  value={paymentStatus}
                  onChange={setPaymentStatus}
                  options={paymentStatusOptions}
                  isClearable
                  placeholder="Select status"
                  styles={customSelectStyles}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button 
                type="submit" 
                variant="primary" 
                className="w-100"
              >
                <FaSearch /> Filter
              </Button>
            </Col>
          </Row>
        </Form>
      </div>

      {loading && (
        <div className="dso-loading-state">
          <FaSpinner className="dso-spinner" />
          <span>Loading invoices...</span>
        </div>
      )}

      {error && (
        <div className="dso-error-state">
          <FaExclamationTriangle className="dso-error-icon" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && invoices.length === 0 && (
        <div className="dso-no-data">No invoices found.</div>
      )}
      
      {!loading && !error && invoices.length > 0 && (
        <>
          <div className="dso-list-table-container">
            <table className="dso-list-table">
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Date Generated</th>
                  <th>Amount</th>
                  <th>Case Count</th>
                  <th>Approval Status</th>
                  <th>Payment Status</th>
                  <th>Payment Date</th>
                  <th>Options</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr key={invoice.id}>
                    <td>
                      <Link
                        to={`/dso/invoices/${invoice.id}/details`} // Link to the details page
                        className="invoice-link"
                        title="View invoice details"
                      >
                        {invoice.invoice_no || 'N/A'}
                      </Link>
                    </td>
                    <td>{formatDate(invoice.created_at)}</td>
                    <td>{formatCurrency(invoice.amount)}</td>
                    <td>{invoice.case_count}</td>
                    {/* Approval status */}
                    <td>{renderApprovalStatusBadge(invoice.invoice_status_str)}</td>
                    {/* Payment status */}
                    <td>{renderPaymentStatusBadge(invoice.payment_status_str)}</td>
                    <td>{formatDate(invoice.payment_date)}</td>
                    <td>
                      <div className="dso-action-buttons">
                        <button
                          onClick={() => navigate(`/dso/invoices/${invoice.id}/details`)}
                          className="dso-icon-button dso-button-view"
                          title="View Invoice"
                        >
                          <FaEye />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsCount={totalInvoices}
            itemsPerPage={entriesPerPage}
          />
        </>
      )}

      {/* Add common list page styles here or import from a CSS file */}
      <style jsx>{`
        .dso-list-page {
          padding: 12px;
          background-color: #f8fafc;
          max-width: 100%;
          overflow-x: hidden;
        }
        .dso-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
          padding-bottom: 10px;
          border-bottom: 1px solid #e2e8f0;
        }
        .dso-list-header h2 {
          margin: 0;
          font-size: 1.3rem;
          font-weight: 600;
          color: #1e293b;
        }
        .filter-section {
          background-color: #ffffff;
          padding: 12px 12px 6px;
          margin-bottom: 12px;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }
        .filter-section .row {
          margin-bottom: 0 !important;
        }
        .filter-section .form-group {
          margin-bottom: 8px;
        }
        .filter-section .form-label {
          margin-bottom: 4px;
          font-size: 0.8rem;
          color: #64748b;
          font-weight: 500;
        }
        .filter-section .form-control {
          height: 36px;
          font-size: 0.85rem;
        }
        .filter-section button {
          height: 36px;
          padding: 0 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
        }
        .clear-filters-btn {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 0.8rem;
          padding: 3px 8px;
        }
        .dso-list-table-container {
          background-color: #ffffff;
          border-radius: 6px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
          overflow-x: auto;
          margin-bottom: 8px;
        }
        .dso-list-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }
        .dso-list-table th {
          text-align: left;
          padding: 8px 10px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.03em;
          color: #64748b;
          background-color: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
          height: 36px;
          white-space: nowrap;
        }
        .dso-list-table td {
          padding: 6px 10px;
          border-bottom: 1px solid #f1f5f9;
          color: #334155;
          vertical-align: middle;
          height: 40px;
        }
        .dso-list-table tr:last-child td {
          border-bottom: none;
        }
        .dso-list-table tr:hover {
          background-color: #f8fafc;
        }
        .dso-list-table td:first-child,
        .dso-list-table th:first-child {
          padding-left: 16px;
        }
        .dso-list-table td:last-child,
        .dso-list-table th:last-child {
          padding-right: 16px;
        }

        /* Loading / Error / No Data States */
        .dso-loading-state,
        .dso-error-state,
        .dso-no-data {
          padding: 28px 0;
          text-align: center;
          color: #64748b;
          font-size: 0.9rem;
          background-color: #fff;
          border-radius: 6px;
          margin-top: 12px;
        }
        .dso-loading-state,
        .dso-error-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 10px;
        }
        .dso-spinner {
          font-size: 1.5rem;
          color: #6366f1;
          animation: spin 1s linear infinite;
        }
        .dso-error-icon {
          font-size: 1.5rem;
          color: #ef4444;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Status Badges */
        .dso-status-badge {
          display: inline-block;
          padding: 4px 8px;
          font-size: 0.7rem;
          font-weight: 600;
          line-height: 1;
          color: #fff;
          text-align: center;
          white-space: nowrap;
          vertical-align: baseline;
          border-radius: 3px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          text-transform: capitalize;
        }
        .dso-status-approved { 
          background-color: #10b981;
          background-image: linear-gradient(to bottom, #10b981, #059669);
        }
        .dso-status-pending { 
          background-color: #f59e0b;
          background-image: linear-gradient(to bottom, #f59e0b, #d97706);
        }
        .dso-status-rejected { 
          background-color: #ef4444;
          background-image: linear-gradient(to bottom, #ef4444, #dc2626);
        }
        .dso-payment-status-completed { 
          background-color: #10b981;
          background-image: linear-gradient(to bottom, #10b981, #059669);
        }
        .dso-payment-status-failed { 
          background-color: #ef4444;
          background-image: linear-gradient(to bottom, #ef4444, #dc2626);
        }
        .dso-payment-status-generated { 
          background-color: #3b82f6;
          background-image: linear-gradient(to bottom, #3b82f6, #2563eb);
        }
        .dso-status-unknown { background-color: #94a3b8; }

        /* Action Buttons */
        .dso-action-buttons {
          display: flex;
          gap: 6px;
          flex-wrap: nowrap;
          justify-content: flex-start;
        }
        /* Icon Buttons */
        .dso-icon-button {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all 0.2s ease;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          padding: 0;
          position: relative;
        }
        .dso-icon-button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
          box-shadow: none;
        }
        .dso-icon-button:active:not(:disabled) {
          transform: scale(0.95);
        }
        
        /* View Button (Blue) */
        .dso-button-view {
          background: linear-gradient(to bottom, #60a5fa, #3b82f6);
          color: #fff;
        }
        .dso-button-view:hover:not(:disabled) {
          background: linear-gradient(to bottom, #3b82f6, #2563eb);
          box-shadow: 0 2px 4px rgba(59,130,246,0.3);
        }
        
        /* Loading state for buttons */
        .dso-icon-button.loading {
          pointer-events: none;
        }
        .dso-icon-button.loading:after {
          content: '';
          position: absolute;
          width: 70%;
          height: 70%;
          border: 2px solid rgba(255,255,255,0.2);
          border-radius: 50%;
          border-top-color: #fff;
          animation: spin 0.8s linear infinite;
        }
        
        /* Invoice link styles */
        .invoice-link {
          color: #3b82f6;
          text-decoration: underline;
          cursor: pointer;
          font-weight: 500;
        }
        .invoice-link:hover {
          color: #2563eb;
        }
      `}</style>
    </div>
  );
}

export default DSOInvoices;